import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../firebase/config';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { FaSave, FaArrowLeft, FaSpinner, FaTimes, FaImage, FaTag, FaCheck, FaGlobe, FaAlignLeft, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { slugify } from '../../../../utilities/helpers';
import { compressImage, formatFileSize } from '../../../../services/compression';
import './EditNews.scss';

const EditNews = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const categories = [
    { id: 1, name: "Technology", value: "technology" },
    { id: 3, name: "Funding", value: "funding" },
    { id: 4, name: "Careers", value: "careers" },
    { id: 2, name: "Partnerships", value: "partnerships" },
    { id: 5, name: "Media Coverage", value: "media-coverage" },
  ];
  
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveRequired, setSaveRequired] = useState(false);
  const [error, setError] = useState(null);
  const [currentTag, setCurrentTag] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [activeSection, setActiveSection] = useState('content');
  const [previewMode, setPreviewMode] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link', 'image'],
      [{ align: [] }],
      ['clean'],
    ],
  };
  
  useEffect(() => {
    fetchNews();
  }, [id]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saveRequired) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveRequired]);
  
  const fetchNews = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'news', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setNews({
          id: docSnap.id,
          ...docSnap.data(),
          tags: docSnap.data().tags || []
        });
      } else {
        setError('News article not found.');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to fetch news article. Please try again.');
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setSaveRequired(true);
    
    // Auto-generate slug when title changes if slug is empty or matches the previous title
    if (name === 'title' && (!news.slug || news.slug === slugify(news.title))) {
      setNews({
        ...news,
        title: value,
        slug: slugify(value)
      });
    } else {
      setNews({
        ...news,
        [name]: value
      });
    }
  };
  
  const handleContentChange = (data) => {
    setSaveRequired(true);
    setNews({
      ...news,
      content: data
    });
  };
  
  const handleStatusChange = (e) => {
    setSaveRequired(true);
    setNews({
      ...news,
      status: e.target.value
    });
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePath(URL.createObjectURL(file));
      setSaveRequired(true);
    }
  };
  
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && currentTag.trim()) {
      e.preventDefault();
      addTag();
    }
  };
  
  const addTag = () => {
    if (!currentTag.trim()) return;
    
    if (!news.tags.includes(currentTag.trim())) {
      setSaveRequired(true);
      setNews({
        ...news,
        tags: [...news.tags, currentTag.trim()]
      });
    }
    setCurrentTag('');
  };
  
  const removeTag = (tagToRemove) => {
    setSaveRequired(true);
    setNews({
      ...news,
      tags: news.tags.filter(tag => tag !== tagToRemove)
    });
  };
  
  const handleSave = async () => {
    if (!news.title || !news.slug) {
      setError('Title and slug are required.');
      return;
    }
    
    try {
      setSaving(true);
      const newsRef = doc(db, 'news', id);
      
      // Handle image upload if there's a new image
      let featuredImage = news.featuredImage;
      if (imageFile) {
        // Compress image before uploading
        setCompressing(true);
        setCompressionProgress(0);
        
        try {
          const compressionResult = await compressImage(imageFile, (progress) => {
            setCompressionProgress(progress);
          });
          
          // Use compressed file if compression was successful, otherwise use original
          const fileToUpload = compressionResult.compressed !== false 
            ? compressionResult 
            : imageFile;
          
          if (compressionResult.compressed && compressionResult.compressionStats) {
            const stats = compressionResult.compressionStats;
            console.log(`✅ Image compressed: ${formatFileSize(stats.originalSize)} → ${formatFileSize(stats.compressedSize)} (${stats.compressionRatio}% saved)`);
          }
          
          const storageRef = ref(storage, `news/${id}/featured-${Date.now()}`);
          await uploadBytes(storageRef, fileToUpload);
          featuredImage = await getDownloadURL(storageRef);
        } catch (compressionError) {
          console.warn('⚠️ Compression failed or skipped, uploading original image:', compressionError);
          // If compression fails, upload original file
          const storageRef = ref(storage, `news/${id}/featured-${Date.now()}`);
          await uploadBytes(storageRef, imageFile);
          featuredImage = await getDownloadURL(storageRef);
        } finally {
          setCompressing(false);
          setCompressionProgress(0);
        }
      }
      
      // Update the news article
      await updateDoc(newsRef, {
        ...news,
        featuredImage,
        updatedAt: serverTimestamp()
      });
      
      setSaveRequired(false);
      setSaving(false);
      setError(null);
      
      // Show success message
      alert('News article saved successfully!');
      
      // Refresh the news data
      fetchNews();
    } catch (err) {
      console.error('Error saving news:', err);
      setError('Failed to save changes. Please try again.');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading news article...</p>
      </div>
    );
  }
  
  if (error && !news) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={() => navigate('/admin/news')}>Back to News</button>
      </div>
    );
  }
  
  const renderContentPreview = () => {
    return (
      <div 
        className="news-content-preview" 
        dangerouslySetInnerHTML={{ __html: news.content }}
      />
    );
  };
  
  return (
    <div className="edit-news-container">
      <div className="edit-news-header">
        <div className="header-left">
          <button 
            className="back-button"
            onClick={() => navigate('/admin/news')}
          >
            <FaArrowLeft /> Back
          </button>
          <h1 title={news.title || 'Edit News'}>{news.title || 'Edit News'}</h1>
        </div>
        <div className="header-right">
          <button 
            className={`preview-button ${previewMode ? 'active' : ''}`}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? 'Edit Mode' : 'Preview Mode'}
          </button>
          {saveRequired && (
            <div className="save-notification">
              <FaExclamationTriangle /> Unsaved Changes
            </div>
          )}
          {compressing && (
            <div className="compression-notification">
              <FaSpinner className="spinner" /> Compressing image... {compressionProgress}%
            </div>
          )}
          <button 
            className={`save-button ${saveRequired ? 'required' : ''}`} 
            onClick={handleSave}
            disabled={saving || compressing}
          >
            {saving || compressing ? <FaSpinner className="spinner" /> : <FaSave />} {saveRequired ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>
      
      {error && <div className="error-message"><FaExclamationTriangle /> {error}</div>}
      
      <div className="edit-news-tabs">
        <button 
          className={`tab-button ${activeSection === 'content' ? 'active' : ''}`} 
          onClick={() => setActiveSection('content')}
        >
          <FaAlignLeft /> Content
        </button>
        <button 
          className={`tab-button ${activeSection === 'seo' ? 'active' : ''}`} 
          onClick={() => setActiveSection('seo')}
        >
          <FaGlobe /> SEO
        </button>
        <button 
          className={`tab-button ${activeSection === 'meta' ? 'active' : ''}`} 
          onClick={() => setActiveSection('meta')}
        >
          <FaInfoCircle /> Settings
        </button>
      </div>
      
      <div className="edit-news-content">
        {!previewMode ? (
          <>
            <div className={`main-content ${activeSection !== 'content' ? 'hidden' : ''}`}>
              <div className="form-group">
                <label htmlFor="title">News Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={news.title || ''}
                  onChange={handleChange}
                  placeholder="Enter a descriptive title for your news article"
                  maxLength={100}
                />
                {news.title && (
                  <small>{news.title.length}/100 characters</small>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="slug">URL Slug</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={news.slug || ''}
                  onChange={handleChange}
                  placeholder="Enter URL slug"
                />
                <small>The URL-friendly version of the title.</small>
              </div>
              
              <div className="form-group">
                <label>News Content</label>
                {activeSection === 'content' && (
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    value={news.content || ''}
                    onChange={handleContentChange}
                    onBlur={() => setSaveRequired(true)}
                  />
                )}
              </div>
            </div>
            
            <div className={`seo-content ${activeSection !== 'seo' ? 'hidden' : ''}`}>
              <div className="form-group">
                <label htmlFor="metaTitle">
                  <FaGlobe className="field-icon" /> Meta Title
                </label>
                <input
                  type="text"
                  id="metaTitle"
                  name="metaTitle"
                  value={news.metaTitle || ''}
                  onChange={handleChange}
                  placeholder="Enter meta title for SEO"
                />
                <small>Recommended length: 50-60 characters</small>
                {news.metaTitle && (
                  <div className="meta-preview">
                    <div className="meta-title">{news.metaTitle}</div>
                    <div className="meta-url">example.com/{news.slug}</div>
                    <div className="meta-description">{news.metaDescription}</div>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="metaDescription">
                  <FaAlignLeft className="field-icon" /> Meta Description
                </label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={news.metaDescription || ''}
                  onChange={handleChange}
                  placeholder="Enter meta description for SEO"
                  rows="3"
                />
                <small>Recommended length: 150-160 characters</small>
              </div>
            </div>
            
            <div className={`meta-content ${activeSection !== 'meta' ? 'hidden' : ''}`}>
              <div className="meta-column">
                <div className="sidebar-section">
                  <h3><FaCheck className="section-icon" /> Publishing</h3>
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={news.status || 'draft'}
                      onChange={handleStatusChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  
                  {news.createdAt && (
                    <div className="news-dates">
                      <div className="date-info">
                        <span>Created:</span> 
                        <span>{new Date(news.createdAt.seconds * 1000).toLocaleString()}</span>
                      </div>
                      {news.updatedAt && (
                        <div className="date-info">
                          <span>Updated:</span> 
                          <span>{new Date(news.updatedAt.seconds * 1000).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="sidebar-section">
                  <h3><FaTag className="section-icon" /> Tags</h3>
                  <div className="tags-input-container">
                    <input
                      type="text"
                      value={currentTag}
                      onChange={(e) => setCurrentTag(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add a tag"
                    />
                    <button 
                      type="button"
                      className="add-tag-btn"
                      onClick={addTag}
                    >
                      Add
                    </button>
                  </div>
                  
                  {news.tags.length > 0 && (
                    <div className="tags-list">
                      {news.tags.map((tag, index) => (
                        <span key={index} className="tag">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="remove-tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="meta-column">
                <div className="sidebar-section">
                  <h3><FaImage className="section-icon" /> Featured Image</h3>
                  <div className="featured-image">
                    {(news.featuredImage || imagePath) ? (
                      <div className="image-preview">
                        <img 
                          src={imagePath || news.featuredImage} 
                          alt={news.title} 
                        />
                        <button 
                          className="remove-image" 
                          onClick={() => {
                            setImagePath('');
                            setImageFile(null);
                            setNews({
                              ...news,
                              featuredImage: ''
                            });
                            setSaveRequired(true);
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <div className="no-image-preview">
                        <FaImage />
                        <span>No image selected</span>
                      </div>
                    )}
                    <label className="custom-file-upload">
                      <input
                        type="file"
                        id="featuredImage"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      Choose Featured Image
                    </label>
                    <small>Recommended size: 1200×630 pixels</small>
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3>Additional Settings</h3>
                  <div className="form-group">
                    <label htmlFor="category">Category</label>
                    <select
                      id="category"
                      name="category"
                      value={news.category || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.value}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="author">Author</label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={news.author || ''}
                      onChange={handleChange}
                      placeholder="Enter author name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="excerpt">Excerpt</label>
                    <textarea
                      id="excerpt"
                      name="excerpt"
                      value={news.excerpt || ''}
                      onChange={handleChange}
                      placeholder="Brief excerpt or summary"
                      rows="3"
                    />
                    <small>A short summary of your news article (optional)</small>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="preview-container">
            <div className="preview-header">
              <h2 title={news.title || 'Untitled News'}>
                {news.title || 'Untitled News'}
              </h2>
              {news.tags && news.tags.length > 0 && (
                <div className="preview-tags">
                  {news.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            
            {(news.featuredImage || imagePath) && (
              <div className="preview-featured-image">
                <img src={imagePath || news.featuredImage} alt={news.title} />
              </div>
            )}
            
            <div className="preview-content">
              {news.content ? (
                renderContentPreview()
              ) : (
                <div className="no-content">
                  <p>No content to preview yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditNews; 